import type { KanbanCardLink, KanbanCardSummary } from '../types/kanban';

/** Outbound `kanban_card` link from parent → child card id. */
function parentToChildKanbanCardLinkIds(links: KanbanCardLink[] | undefined): string[] {
  if (!links?.length) return [];
  return links.filter((l) => l.link_type === 'kanban_card').map((l) => l.link_target_id);
}

/**
 * For each card id, the set of ids that should move with it (descendants via `kanban_card` links),
 * restricted to cards in the same list + swimlane bucket as the root.
 */
export function buildDescendantIdsByCardId(
  cards: KanbanCardSummary[],
  swimlaneBucket: string | undefined,
): Map<string, Set<string>> {
  const byId = new Map<string, KanbanCardSummary>();
  for (const c of cards) byId.set(c.id, c);

  const inSameBucket = (c: KanbanCardSummary) => {
    if (swimlaneBucket === undefined) return true;
    const sk = c.swimlane_id ?? '__unsorted__';
    return sk === swimlaneBucket;
  };

  const childrenByParent = new Map<string, string[]>();
  for (const c of cards) {
    if (!inSameBucket(c)) continue;
    const targets = parentToChildKanbanCardLinkIds(c.links);
    if (!targets.length) continue;
    const list = childrenByParent.get(c.id) ?? [];
    for (const tid of targets) {
      const child = byId.get(tid);
      if (!child || child.list_id !== c.list_id || !inSameBucket(child)) continue;
      if (child.card_type !== 'subtask') continue;
      if (!list.includes(tid)) list.push(tid);
    }
    if (list.length) childrenByParent.set(c.id, list);
  }

  const memo = new Map<string, Set<string>>();

  function descendantsOf(rootId: string): Set<string> {
    const hit = memo.get(rootId);
    if (hit) return hit;
    const acc = new Set<string>();
    const stack = [...(childrenByParent.get(rootId) ?? [])];
    while (stack.length) {
      const id = stack.pop()!;
      if (acc.has(id)) continue;
      acc.add(id);
      const next = childrenByParent.get(id);
      if (next) for (const n of next) stack.push(n);
    }
    memo.set(rootId, acc);
    return acc;
  }

  const out = new Map<string, Set<string>>();
  for (const c of cards) {
    if (!inSameBucket(c)) continue;
    out.set(c.id, descendantsOf(c.id));
  }
  return out;
}

export type BoardCardStackRow =
  | { kind: 'single'; card: KanbanCardSummary }
  | { kind: 'stack'; root: KanbanCardSummary; nested: KanbanCardSummary[] };

function stackRowRootCard(row: BoardCardStackRow): KanbanCardSummary {
  return row.kind === 'stack' ? row.root : row.card;
}

/** Index of the last row whose primary card is not a subtask (stack anchor), or -1. */
function lastNonSubtaskStackAnchorIndex(rows: BoardCardStackRow[]): number {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (stackRowRootCard(rows[i]).card_type !== 'subtask') return i;
  }
  return -1;
}

/**
 * Order-preserving: a subtask that appears under a prior card in the column is merged into that card's stack.
 */
export function buildBoardCardStackRows(
  orderedCards: KanbanCardSummary[],
  descendantIdsByCardId: Map<string, Set<string>>,
): BoardCardStackRow[] {
  const rows: BoardCardStackRow[] = [];
  let currentStack: KanbanCardSummary[] | null = null;

  for (const card of orderedCards) {
    const parentId = findStackParentInRows(rows, card, descendantIdsByCardId);
    if (parentId) {
      const parentRow = rows[rows.length - 1];
      if (parentRow?.kind === 'stack' && parentRow.root.id === parentId) {
        parentRow.nested.push(card);
        continue;
      }
      if (parentRow?.kind === 'single' && parentRow.card.id === parentId) {
        const root = parentRow.card;
        rows[rows.length - 1] = { kind: 'stack', root, nested: [card] };
        continue;
      }
    }

    if (card.card_type === 'subtask') {
      if (currentStack) {
        currentStack.push(card);
        continue;
      }
      currentStack = [card];
      continue;
    }

    if (currentStack) {
      rows.push({ kind: 'stack', root: card, nested: currentStack });
      currentStack = null;
    } else {
      rows.push({ kind: 'single', card });
    }
  }

  // Trailing subtasks after the last non-subtask card (e.g. story → epic → subtask): attach to that anchor
  // instead of rendering them as separate full cards.
  if (currentStack?.length) {
    const anchorIdx = lastNonSubtaskStackAnchorIndex(rows);
    if (anchorIdx >= 0) {
      const anchor = rows[anchorIdx];
      if (anchor.kind === 'single') {
        rows[anchorIdx] = { kind: 'stack', root: anchor.card, nested: [...currentStack] };
      } else {
        anchor.nested.push(...currentStack);
      }
    } else {
      for (const orphan of currentStack) {
        rows.push({ kind: 'single', card: orphan });
      }
    }
  }

  return rows;
}

function findStackParentInRows(
  rows: BoardCardStackRow[],
  sub: KanbanCardSummary,
  descendantIdsByCardId: Map<string, Set<string>>,
): string | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    const candidate = row.kind === 'stack' ? row.root : row.card;
    const desc = descendantIdsByCardId.get(candidate.id);
    if (desc?.has(sub.id)) return candidate.id;
  }
  return null;
}

export function stackRowCardIds(row: BoardCardStackRow): string[] {
  if (row.kind === 'single') return [row.card.id];
  return [row.root.id, ...row.nested.map((c) => c.id)];
}

export function stackRowPrimaryId(row: BoardCardStackRow): string {
  return row.kind === 'single' ? row.card.id : row.root.id;
}

/** Flat card order within a column (root then each nested subtask for stacks). Used for drag insert index and positions. */
export function flattenStackRowsToOrderedCardIds(rows: BoardCardStackRow[]): string[] {
  const out: string[] = [];
  for (const row of rows) {
    if (row.kind === 'single') {
      out.push(row.card.id);
    } else {
      out.push(row.root.id, ...row.nested.map((s) => s.id));
    }
  }
  return out;
}

/**
 * After inserting `draggedId` at `insertAt` in `flatWithoutDragged`, if the card immediately below
 * is a subtask that was nested under the card immediately above (same stack root), return that
 * subtask id so it can be relinked to the dragged card (e.g. drag task between epic and its subtask).
 */
export function findSubtaskToReassignBelowInsert(
  flatWithoutDragged: string[],
  insertAt: number,
  draggedId: string,
  cardById: Map<string, Pick<KanbanCardSummary, 'id' | 'card_type'>>,
): string | null {
  if (insertAt < 0 || insertAt > flatWithoutDragged.length) return null;
  const belowId = insertAt < flatWithoutDragged.length ? flatWithoutDragged[insertAt] : null;
  if (!belowId) return null;
  const below = cardById.get(belowId);
  if (!below || below.card_type !== 'subtask') return null;
  const aboveId = insertAt > 0 ? flatWithoutDragged[insertAt - 1] : null;
  if (!aboveId || aboveId === draggedId) return null;
  const above = cardById.get(aboveId);
  if (!above || above.card_type === 'subtask') return null;
  return belowId;
}

/**
 * Where to show an intra-stack drop line for `insertIdx` (index into `flatWithoutDragged`),
 * or null if the insert is not inside this stack's root+nested block.
 * Flat order is [root, ...nested]; valid internal slots are after root or after each nested card.
 */
export function intraStackDropSlot(
  row: BoardCardStackRow,
  flatWithoutDragged: string[],
  insertIdx: number,
): 'afterRoot' | { afterNestedIndex: number } | null {
  if (row.kind !== 'stack' || row.nested.length === 0) return null;
  const r0 = flatWithoutDragged.indexOf(row.root.id);
  if (r0 < 0) return null;
  const n = row.nested.length;
  const slotIndex = insertIdx - r0 - 1; // 1 = after root; n = after second-to-last nested; not after whole stack
  if (slotIndex < 1 || slotIndex > n) return null;
  if (slotIndex === 1) return 'afterRoot';
  return { afterNestedIndex: slotIndex - 2 };
}

function splitStackForGhostInsert(
  row: Extract<BoardCardStackRow, { kind: 'stack' }>,
  offsetInRow: number,
  ghost: BoardCardStackRow,
): BoardCardStackRow[] {
  const { root, nested } = row;
  const len = 1 + nested.length;
  if (offsetInRow <= 0) return [ghost, row];
  if (offsetInRow >= len) return [row, ghost];
  const before = nested.slice(0, offsetInRow - 1);
  const pivot = nested[offsetInRow - 1]!;
  const afterRest = nested.slice(offsetInRow);
  const left: BoardCardStackRow =
    before.length === 0 ? { kind: 'single', card: root } : { kind: 'stack', root, nested: before };
  const right: BoardCardStackRow =
    afterRest.length === 0 ? { kind: 'single', card: pivot } : { kind: 'stack', root: pivot, nested: afterRest };
  return [left, ghost, right];
}

/** Insert `ghost` at flat index `insertAt` into `rows` (flat = flatten(rows), indices 0..flat.length). */
export function stackRowsWithGhostAtFlatIndex(
  rows: BoardCardStackRow[],
  ghost: BoardCardStackRow,
  insertAt: number,
): BoardCardStackRow[] {
  let pos = 0;
  const out: BoardCardStackRow[] = [];
  let placed = false;
  for (const row of rows) {
    const len = stackRowCardIds(row).length;
    const rowEnd = pos + len;
    if (!placed && insertAt < rowEnd) {
      if (row.kind === 'single') {
        if (insertAt === pos) out.push(ghost, row);
        else out.push(row, ghost);
      } else {
        out.push(...splitStackForGhostInsert(row, insertAt - pos, ghost));
      }
      placed = true;
    } else {
      out.push(row);
    }
    pos = rowEnd;
  }
  if (!placed) out.push(ghost);
  return out;
}

/** Row used for drag preview when dragging a card that may be nested under a stack root. */
export function stackRowForDraggedCardId(
  stackRows: BoardCardStackRow[],
  cardId: string,
): BoardCardStackRow | undefined {
  for (const row of stackRows) {
    if (row.kind === 'single') {
      if (row.card.id === cardId) return row;
      continue;
    }
    if (row.root.id === cardId) return row;
    const sub = row.nested.find((s) => s.id === cardId);
    if (sub) return { kind: 'single', card: sub };
  }
  return undefined;
}

/** Rows to show while dragging: remove dragged cards without hiding an entire stack when only a nested card moves. */
export function stackRowsWithoutMovedCards(
  stackRows: BoardCardStackRow[],
  movedSet: Set<string>,
): BoardCardStackRow[] {
  const out: BoardCardStackRow[] = [];
  for (const row of stackRows) {
    if (row.kind === 'single') {
      if (!movedSet.has(row.card.id)) out.push(row);
      continue;
    }
    if (movedSet.has(row.root.id)) continue;
    const remainingNested = row.nested.filter((s) => !movedSet.has(s.id));
    if (remainingNested.length === row.nested.length) {
      out.push(row);
    } else if (remainingNested.length === 0) {
      out.push({ kind: 'single', card: row.root });
    } else {
      out.push({ kind: 'stack', root: row.root, nested: remainingNested });
    }
  }
  return out;
}
